import type { IndicatorScope } from '@Application/Indicators/IndicatorScope';
import countScopeCoverage, {
	type ScopeCoverage,
} from '@UI/Pages/Home/Hooks/useHomeIndicatorScope/countScopeCoverage';
import resolveEffectiveHomeScope from '@UI/Pages/Home/Hooks/useHomeIndicatorScope/resolveEffectiveHomeScope';
import resolveIndicatorScope from '@UI/Pages/Home/Hooks/useHomeIndicatorScope/resolveIndicatorScope';
import homeScopeSignal, { type HomeScope } from '@UI/Pages/Home/Signals/homeScopeSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';

export type HomeIndicatorScope = {
	/** `loading` until the caller's organizations are known; `unavailable` without account or agency. */
	readonly status: 'loading' | 'unavailable' | 'ready';
	readonly scope: HomeScope;
	/** `null` unless `status` is `ready`: no request may be built without it. */
	readonly indicatorScope: IndicatorScope | null;
	readonly coverage: ScopeCoverage;
};

/**
 * Resolves the Périmètre filter against the caller's own organizations (never the current
 * organization's agencies alone) into the scope the indicator requests are made for.
 */
const useHomeIndicatorScope = (): HomeIndicatorScope => {
	const { user } = useStrictAuthenticatedUser();
	const { organizations, isResolved } = useCurrentOrganization();
	const storedScope = useSignalValue(homeScopeSignal);
	const accountId = user.account?.id ?? null;

	const scope = React.useMemo(
		() => resolveEffectiveHomeScope(organizations, storedScope),
		[organizations, storedScope],
	);
	const coverage = React.useMemo(() => countScopeCoverage(organizations, scope), [organizations, scope]);
	const scopeKind = scope.kind;
	const scopeId = scope.kind === 'all' ? null : scope.id;

	const indicatorScope = React.useMemo<IndicatorScope | null>(() => {
		if (accountId === null || coverage.facilityCount === 0) {
			return null;
		}

		return resolveIndicatorScope(
			scopeKind === 'all' || scopeId === null ? { kind: 'all' } : { kind: scopeKind, id: scopeId },
			accountId,
		);
	}, [accountId, coverage.facilityCount, scopeKind, scopeId]);

	const status = React.useMemo<HomeIndicatorScope['status']>(() => {
		if (indicatorScope !== null) {
			return 'ready';
		}

		return isResolved ? 'unavailable' : 'loading';
	}, [indicatorScope, isResolved]);

	return { status, scope, indicatorScope, coverage };
};

export default useHomeIndicatorScope;
